import useTheme from "../../../../../common/hooks/useTheme";
import CircularGraph from "../../common/CircularGraph";
import React from "react";
import { useSelector } from "react-redux";
import { RootStatePlatform } from "../../../store/platform-slice";
import ScoreMessageInfoPanel from "../ScoreMessageInfoPanel";
import ItemCardHome from "../ItemCardHome";
import { useAppStore } from "../../../../../providers/AppStore";
import {
  getShortTimeValue,
  getZoneRange,
  PNOE_WORKOUT_NAME,
} from "../../../store/helper";
import BreakdownBarChart from "../BreakdownBarChart";
import FuelUsage from "../FuelUsage";
import { format } from "date-fns";
import { Box } from "@mui/material";
import BlurContainer from "../../../../../common/ui/BlurContainer";
import { useAuth } from "../../../../../providers/AuthProvider";

type TrainingZoneObj = {
  zone: number;
  duration_in_minutes: number;
  range: {
    min_value: number;
    max_value: number;
  };
  fat: number;
  carbs: number;
};

const ActivityTab = () => {
  const theme = useTheme();
  const { ASIsIntegrationActive } = useAppStore();
  const { AuthUser, AuthIsSimpleUser } = useAuth();
  const { ASSelectedClient } = useAppStore();

  const ASActiveDate = useSelector(
    (state: RootStatePlatform) => state.platform.ASActiveDate
  );

  const homeData = useSelector(
    (state: RootStatePlatform) => state.platform.dataNutServer.activity_tab
  );
  const hasReportDate = useSelector(
    (state: RootStatePlatform) =>
      state.platform.dataNutServer.overview_tab.pnoe_metrics.report_date
  );

  const scheduledActivities = homeData.activities.filter(
    (el: any) => el.goal.target !== 0
  );

  const otherActivities = homeData.activities
    .filter((el: any) => el.name !== 0)
    .filter((el: any) => el.goal.target === 0);

  const getActivityColorCodingType = (score: number = 0) => {
    const scheduledActivities = homeData.activities.filter(
      (el: any) => el.goal.target !== 0
    );
    const scheduledActivitiesIsCompleted = scheduledActivities.every(
      (activity: any) => activity.is_completed === true
    );
    if (score === 0) {
      return 3;
    } else if (score < 50) {
      return scheduledActivitiesIsCompleted ? 4 : 5;
    } else if (score < 80) {
      return scheduledActivitiesIsCompleted ? 1 : 2;
    } else {
      return scheduledActivitiesIsCompleted ? 1 : 2;
    }
  };

  const activityBreakdownData = () => {
    const totalWorkoutCals = Math.round(
      homeData.activities.reduce(
        (sum: number, el: any) => sum + el.goal.current,
        0
      )
    );

    const percentage1 = Math.round(
      (homeData.general_activities.current * 100) / homeData.total_burn.current
    );

    const percentage3 = Math.round(
      (homeData.resting_cals.current * 100) / homeData.total_burn.current
    );

    return [
      {
        index: 0,
        label: "Workout",
        value: totalWorkoutCals,
        percentage: `${100 - percentage1 - percentage3}%`,
        unit: "Kcal",
      },
      {
        index: 1,
        label: "General activity",
        value: Math.round(homeData.general_activities.current),
        percentage: `${percentage1}%`,
        unit: "Kcal",
      },
      {
        index: 2,
        label: "Resting metabolic rate",
        value: Math.round(homeData.resting_cals.current),
        percentage: `${percentage3}%`,
        unit: "Kcal",
      },
    ];
  };

  const calculateZonePercentages = (data: TrainingZoneObj[]) => {
    // Filter out zones with duration greater than 0
    const zonesWithDuration = data.filter((obj) => obj.duration_in_minutes > 0);

    // Calculate total duration for zones with duration greater than 0
    const totalDuration = zonesWithDuration.reduce(
      (acc, obj) => acc + obj.duration_in_minutes,
      0
    );

    // Calculate percentage for each zone with duration greater than 0
    let zonePercentages = zonesWithDuration.map((obj) => ({
      zone: obj.zone,
      percentage: Math.round((obj.duration_in_minutes / totalDuration) * 100),
    }));

    // Check if sum of percentages exceeds 100
    const sumPercentages = zonePercentages.reduce(
      (acc, obj) => acc + obj.percentage,
      0
    );
    if (sumPercentages > 100) {
      // Find the last zone with duration greater than 0
      const lastZoneIndex = zonePercentages.length - 1;
      // Adjust the percentage of the last zone with non-zero duration
      zonePercentages[lastZoneIndex].percentage -= sumPercentages - 100;
    }

    return zonePercentages;
  };

  const zonePercentages = calculateZonePercentages(homeData.training_zones);

  const trainingZonesBreakdownData = homeData.training_zones.map(
    (zone: any) => {
      return {
        index: zone.zone - 1,
        label: `Zone ${zone.zone} ${getZoneRange(zone.range, zone.zone)}`,
        value: getShortTimeValue(zone.duration_in_minutes),
        percentage:
          zonePercentages.find(
            (el: { zone: number; percentage: number }) => el.zone === zone.zone
          ) !== undefined
            ? `${
                zonePercentages.find(
                  (el: { zone: number; percentage: number }) =>
                    el.zone === zone.zone
                )?.percentage
              }%`
            : "0%",
      };
    }
  );

  // TODO: Get weekly values from server
  return (
    <div style={styles.container()}>
      <CircularGraph
        size={121}
        strokeWidth={6}
        value={homeData.score}
        color={theme.colors_ds.PRIMARY_ICON_MAIN}
        backgroundColor={theme.colorTokens.P[10]}
        label={homeData.title}
        isMainChart={true}
      />
      <BlurContainer
        isBlurred={
          AuthIsSimpleUser
            ? !AuthUser.has_advanced_app_access
            : !ASSelectedClient?.has_advanced_app_access
        }
        title={"Please contact your PNOE tester\n to get started"}
        text={""}
        customBorderColor={
          (
            AuthIsSimpleUser
              ? !AuthUser.has_advanced_app_access
              : !ASSelectedClient?.has_advanced_app_access
          )
            ? theme.colorTokens.W[60]
            : undefined
        }
      >
        {AuthIsSimpleUser &&
        ASActiveDate === format(new Date().toISOString(), "yyyy-MM-dd") ? (
          <ScoreMessageInfoPanel
            data={homeData.score_message}
            colorCodingType={getActivityColorCodingType(homeData.score)}
          />
        ) : (
          <Box style={{ height: "24px" }} />
        )}
        <div style={styles.cardsContainer}>
          <ItemCardHome
            label={"Total burn"}
            value={homeData.total_burn.current}
            unit="Kcal"
            maxValue={homeData.total_burn.target}
          />
          <ItemCardHome
            label={"General activities"}
            value={
              !ASIsIntegrationActive()
                ? 0
                : Math.round(homeData.general_activities.current)
            }
            maxValue={Math.round(homeData.general_activities.target)}
            unit="Kcal"
            isDisabled={!ASIsIntegrationActive()}
          />
          <ItemCardHome
            label={"Resting metabolic rate"}
            value={Math.round(homeData.resting_cals.current)}
            maxValue={Math.round(homeData.resting_cals.target)}
            unit="Kcal"
          />
          {/*Scheduled Activities Elements*/}
          {scheduledActivities.map((el: any, index: number) => (
            <ItemCardHome
              key={index}
              label={PNOE_WORKOUT_NAME[el.name]}
              value={Math.round(el.goal.current)}
              maxValue={Math.round(el.goal.target)}
              unit="Kcal"
            />
          ))}
        </div>
        {/*Other Activities Elements*/}
        {otherActivities.length > 0 && (
          <div style={styles.title(theme)}>Other activities</div>
        )}

        {otherActivities.length > 0 && (
          <div style={styles.cardsContainer}>
            {otherActivities.map((el: any, index: number) => (
              <ItemCardHome
                key={index}
                label={PNOE_WORKOUT_NAME[el.name]}
                value={Math.round(el.goal.current)}
                maxValue={undefined}
                unit="Kcal"
                showStatisticsIcon={false}
              />
            ))}
          </div>
        )}
        <BreakdownBarChart
          data={activityBreakdownData()}
          type="withPercentage"
        />
        {hasReportDate && (
          <>
            <div style={styles.title(theme)}>Fuel usage during exercise</div>
            <FuelUsage />
            <div style={styles.title(theme)}>Training zones</div>
            <BreakdownBarChart
              data={trainingZonesBreakdownData}
              type="withDurationAndPercentage"
            />
          </>
        )}
      </BlurContainer>
    </div>
  );
};

export default ActivityTab;

const styles = {
  container: () => ({
    width: "100%",
    paddingTop: "32px",
    display: "flex",
    flexDirection: "column" as "column",
    alignItems: "center",
    justifyContent: "center",
  }),
  title: (theme: any) => ({
    marginTop: "16px",
    marginBottom: "16px",
    alignSelf: "flex-start",
    fontFamily: "var(--font-Regular)",
    fontSize: "14px",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
  }),
  cardsContainer: {
    display: "flex",
    flexDirection: "column" as "column",
    gap: "8px",
  },
};
